import { getCommandHandler } from './commandRegistry';
import { sendAgentEvent } from './eventClient';
import { recordTurnCommand } from './turnTiming';
import { holdOutstanding, isLive } from './sessionLifecycle';

const EVENTS = {
	navigate: ['navigation.completed', 'navigation.failed'],
	open_section: ['section.opened', 'section.failed'],
	resolve_entities: ['resolution.completed', 'resolution.failed'],
	apply_filter: ['filter.applied', 'filter.failed'],
	focus_entity: ['entity.focused', 'entity.focus_failed'],
	open_modal: ['modal.opened', 'modal.failed'],
	open_experience: ['experience.opened', 'experience.failed'],
	read_statistic: ['statistic.read', 'statistic.failed'],
	fetch_dataset: ['dataset.read', 'dataset.failed'],
};
const COMMAND_TIMEOUT_MS = 15000;
const SUPPORTED_COMMAND_VERSION = 1;
const SUPPORTED_PROTOCOL_VERSION = 2;

/**
 * Commands this page has already run, newest last.
 *
 * A reply redelivered, a retry, a double dispatch - each ran the command
 * again and opened a second copy of the same form. Bounded because a page
 * can stay open all day and this is a guard, not a history.
 */
const alreadyRun = new Set();
const REMEMBERED_COMMANDS = 200;

/**
 * Forget which commands have run. For tests, and for a signed-out page.
 * @return {void}
 */
export function resetDispatchGuards() {
	alreadyRun.clear();
}

/**
 * Whether a command's deadline has passed.
 *
 * The backend refuses a callback for an expired command, so running one
 * produces a screen the user did not ask for *and* a report that is thrown
 * away. A command with no deadline is not expired - that is what every
 * command looked like before the protocol carried one.
 *
 * @param {object} command typed command
 * @return {boolean} true when it is too late to run it
 */
function hasExpired(command) {
	if (!command.expires_at) return false;
	const deadline = Date.parse(command.expires_at);
	return Number.isFinite(deadline) && Date.now() > deadline;
}

/**
 * Whether this command belongs to the conversation still on screen.
 *
 * The envelope says which session the command was issued for, and the
 * caller says which one is open now. A command that outlived its
 * conversation lands on top of whatever replaced it. Unstated means
 * unchanged, so nothing breaks where the caller does not track it.
 *
 * @param {object} envelope request/session correlation
 * @return {boolean} true when the conversation has moved on
 */
function sessionHasMovedOn(envelope) {
	// Asked of the lifecycle, not only of the envelope.
	//
	// The envelope is assembled when the chain starts and travels with it,
	// so a conversation reset *during* the chain cannot appear in it - and
	// a caller that simply forgot the field would silently disable the
	// guard, which is how it came to have no production caller at all.
	// Consulting the authority means the check holds whether or not the
	// caller remembered to describe the world correctly.
	if (!isLive(envelope?.session_id, envelope?.session_generation)) return true;
	const active = envelope?.active_session_id;
	return Boolean(active) && Boolean(envelope?.session_id) && active !== envelope.session_id;
}

/**
 * Remember a command as run, forgetting the oldest once the set is full.
 * @param {string} key command identity
 * @return {void}
 */
function remember(key) {
	alreadyRun.add(key);
	while (alreadyRun.size > REMEMBERED_COMMANDS) {
		alreadyRun.delete(alreadyRun.values().next().value);
	}
}

/**
 * Correlation fields a command's events are reported against.
 * @param {object} command semantic agent command
 * @return {object} workflow correlation
 */
function eventCorrelation(command) {
	if ((command.protocol_version || 1) !== SUPPORTED_PROTOCOL_VERSION) {
		throw new Error(`Unsupported command protocol: ${command.protocol_version}`);
	}
	return {
		workflow_instance_id: command.workflow_instance_id,
		workflow_revision: command.workflow_revision,
		idempotency_key: command.idempotency_key,
	};
}

/**
 * Execute one allowlisted semantic command and report its outcome.
 * @param {object} command typed command
 * @param {object} envelope request/session correlation
 * @return {Promise<object>} next agent response
 */
export async function dispatchAgentCommand(command, envelope) {
	const commandStarted = performance.now();
	if ((command.version || 1) !== SUPPORTED_COMMAND_VERSION) {
		throw new Error(`Unsupported command version: ${command.version}`);
	}
	const correlation = eventCorrelation(command);
	const handler = getCommandHandler(command.type);
	const eventTypes = EVENTS[command.type];
	if (!handler || !eventTypes) throw new Error(`Unsupported command: ${command.type}`);
	// Three checks before the command has any effect. Each of them is the
	// difference between doing nothing and doing something nobody asked
	// for, so all three run before the handler rather than around it.
	const identity = command.idempotency_key || command.command_id;
	if (hasExpired(command)) {
		// Reported rather than dropped: the turn is waiting, and the
		// backend has a terminal outcome for exactly this.
		await sendAgentEvent({
			...envelope,
			...correlation,
			command_id: command.command_id,
			event_type: 'command.timed_out',
			result: {},
		}).catch(() => undefined);
		throw new Error(`Command expired: ${command.type}`);
	}
	if (sessionHasMovedOn(envelope)) {
		throw new Error(`Command belongs to another session: ${command.type}`);
	}
	if (identity && alreadyRun.has(identity)) {
		throw new Error(`Command already run: ${command.type}`);
	}
	if (identity) remember(identity);
	let timeoutId;
	// L07. Promise.race rejects the race and leaves the handler running: a
	// modal that opens two seconds after the turn gave up on it is a modal
	// nobody asked for, on top of whatever the user did next. A handler
	// that accepts the signal can stop; one that ignores it is no worse off
	// than before, and the abort is what the next command reads to know the
	// previous one was abandoned rather than finished.
	const abort = new AbortController();
	// Held so a conversation reset can stop this rather than waiting for it
	// to time out. Released whatever happens, below.
	const release = holdOutstanding(abort);
	try {
		const result = await Promise.race([
			// On the envelope as well as positionally: a handler that wants
			// it can take the third argument, and everything that forwards
			// the envelope gets it for free.
			handler(command, {...envelope, signal: abort.signal}, abort.signal),
			new Promise((resolve, reject) => {
				timeoutId = setTimeout(
					() => {
						abort.abort(new Error('command_timed_out'));
						reject(new Error('command_timed_out'));
					},
					COMMAND_TIMEOUT_MS,
				);
			}),
		]);
		clearTimeout(timeoutId);
		release();
		// The handler finished. Whether anyone still wants the result is a
		// different question: a reset during the await leaves this holding
		// an acknowledgement for a conversation that is gone, and reporting
		// it would restart a chain there.
		if (!isLive(envelope?.session_id, envelope?.session_generation)) {
			recordTurnCommand(envelope.turn_id, {
				type: command.type,
				ms: Math.round(performance.now() - commandStarted),
				outcome: 'abandoned',
			});
			return null;
		}
		recordTurnCommand(envelope.turn_id, {
			type: command.type,
			ms: Math.round(performance.now() - commandStarted),
			outcome: 'ok',
		});
		return sendAgentEvent({
			request_id: envelope.request_id,
			command_id: command.command_id,
			session_id: envelope.session_id,
			turn_id: envelope.turn_id || null,
			event_type: eventTypes[0],
			result: result && typeof result === 'object' ? result : {},
			error_code: null,
			...correlation,
		});
	} catch (error) {
		clearTimeout(timeoutId);
		release();
		const timedOut = error?.message === 'command_timed_out';
		// A failure in a conversation nobody is in is not worth reporting
		// back into it.
		if (!isLive(envelope?.session_id, envelope?.session_generation)) return null;
		recordTurnCommand(envelope.turn_id, {
			type: command.type,
			ms: Math.round(performance.now() - commandStarted),
			outcome: timedOut ? 'timed_out' : 'failed',
		});
		return sendAgentEvent({
			request_id: envelope.request_id,
			command_id: command.command_id,
			session_id: envelope.session_id,
			turn_id: envelope.turn_id || null,
			event_type: timedOut ? 'command.timed_out' : eventTypes[1],
			result: {},
			error_code: error?.message || 'command_failed',
			...correlation,
		});
	}
}
